// Control de transferencias — motor de cruce (sin React, sin Supabase: se puede testear solo).
//
// Pregunta que responde: ¿cada venta cobrada por transferencia aparece como CRÉDITO en el
// estado de cuenta de la financiera? Los débitos (comisión 3%, cambio de moneda, pagos) no
// importan para esto y no se cargan.
//
// Cruce venta ↔ crédito, en este orden de confianza:
//   0. COMPROBANTE leído (read-receipt): mismo CUIT de quien pagó, o mismo monto con la fecha del
//      comprobante (−1..+5 días). Seguro si el crédito es igual al monto del comprobante.
//   1. DNI: el crédito trae el CUIT de quien transfirió ("TT-Trn ,11/07/26 12:51,GAL,20-37008565-0,KI")
//      y las órdenes de Tiendanube el DNI del cliente (orders.customer_doc). Medido 10/10 sobre
//      los cruces ya hechos: coincide en el 89% (el resto es otra persona pagando por el cliente).
//   2. Monto exacto (±$1, contra el total o la seña).
//   3. Monto aproximado (±2%: el cliente redondea, "174.650" por "174.297").
// Ventana de fechas (crédito − venta): −3 a +7 días por monto; −5 a +30 por DNI. Medido: el
// 94% de los créditos se acredita el MISMO día de la venta.
//
// "Seguro" (se puede confirmar en tanda): mismo DNI con monto ±2%, o monto exacto cuando ese
// crédito y esa venta solo se tienen el uno al otro. Lo demás es sugerencia: se confirma a mano.

export type Credit = {
    id: number; movement_date: string; amount: number; description: string | null;
    status: 'pendiente' | 'asignado' | 'ignorado' | 'comision'; order_id: number | null;
};

export type TOrder = {
    id: number; source: string; tiendanube_id: string | null; contact_name: string | null;
    total_price: number; deposit_amount: number | null; created_at: string;
    status: string; payment_status: string; gateway_name: string | null; payment_details: any;
    customer_doc: string | null; transfer_excluded_reason: string | null;
    /** Datos leídos del comprobante que mandó el cliente (read-receipt / transfer_receipts). */
    receipts?: ReceiptInfo[];
    /** Cuándo pagó: llegada del comprobante o, si no hay, cuándo se marcó pagada. Es el ANCLA de
     *  la ventana de fechas (una orden de hace días que se transfirió hoy se busca alrededor de hoy). */
    pay_date?: string | null;
};

/** Lo que importa de un comprobante leído: monto y fecha REALES transferidos y CUIT de quien pagó. */
export type ReceiptInfo = { amount: number | null; date: string | null; doc: string | null };

export type MatchKind = 'comprobante' | 'dni' | 'exacto' | 'aprox';
/** diff = crédito − monto de la venta (total o seña); amount = ese monto de la venta.
 *  receiptDiff = crédito − monto del comprobante (solo kind 'comprobante'). */
export type Match = { orderId: number; creditId: number; kind: MatchKind; diff: number; amount: number; days: number; seña: boolean; receiptDiff?: number };

/** Día calendario local (AR) de un timestamp, 'YYYY-MM-DD'. */
export const dayOf = (iso: string) => {
    if (iso.length === 10) return iso;
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const daysBetween = (a: string, b: string) =>
    Math.round((new Date(b + 'T12:00:00').getTime() - new Date(a + 'T12:00:00').getTime()) / 86400000);

/** DNI de quien transfirió, sacado del CUIT de la descripción (20-37008565-0 → 37008565). */
export function payerDoc(desc: string | null | undefined): string | null {
    const m = String(desc || '').match(/\b(\d{2})-(\d{7,8})-(\d)\b/);
    return m ? m[2].replace(/^0+/, '') : null;
}
/** Banco/billetera de origen ("GAL", "MP", "SANT"…) y CUIT, para mostrar. */
export function payerInfo(desc: string | null | undefined): { bank: string | null; cuit: string | null } {
    const s = String(desc || '');
    const cuit = (s.match(/\b\d{2}-\d{7,8}-\d\b/) || [null])[0];
    const bank = (s.match(/\d{2}:\d{2},([A-Z$ ]{2,12}),/) || s.match(/\d{2}\/\d{2}\/\d{2},([A-Z$ ]{2,12}),/) || [])[1] || null;
    return { bank: bank ? bank.trim() : null, cuit };
}
/** DNI a partir de un DNI o CUIT (11 dígitos → los 8 del medio). */
export function docKey(raw: string | null | undefined): string | null {
    const d = String(raw || '').replace(/\D/g, '');
    if (!d) return null;
    return (d.length === 11 ? d.slice(2, 10) : d).replace(/^0+/, '') || null;
}
/** DNI del cliente: si es un CUIT de 11 dígitos, el DNI son los 8 del medio. */
export function orderDoc(o: Pick<TOrder, 'customer_doc'>): string | null {
    const d = String(o.customer_doc || '').replace(/\D/g, '');
    if (!d) return null;
    return (d.length === 11 ? d.slice(2, 10) : d).replace(/^0+/, '') || null;
}

/** Pago Nube "transferencia" cobra Pago Nube, no la financiera: nunca va a estar en el extracto. */
export const isPagoNube = (o: Pick<TOrder, 'source' | 'gateway_name' | 'payment_details'>) =>
    o.source === 'tiendanube' && /^pago\s*nube/i.test(o.gateway_name || '') && o.payment_details?.method === 'wire_transfer';

/** Todas las parejas posibles venta ↔ crédito libre, de la más confiable a la menos. */
export function candidatePairs(orders: TOrder[], credits: Credit[]): Match[] {
    const out: Match[] = [];
    for (const o of orders) {
        const od = dayOf(o.pay_date || o.created_at);
        const doc = orderDoc(o);
        const amounts: Array<[number, boolean]> = [[Number(o.total_price || 0), false]];
        if (Number(o.deposit_amount || 0) > 0) amounts.push([Number(o.deposit_amount), true]);
        for (const c of credits) {
            const days = daysBetween(od, c.movement_date);
            const sameDoc = !!doc && payerDoc(c.description) === doc;
            let best: Match | null = null;
            // 0. Comprobante leído: mismo CUIT de quien pagó, o mismo monto con fecha pegada a la del
            //    comprobante. Es la evidencia más fuerte (sirve también en ventas manuales sin DNI).
            for (const r of (o.receipts || [])) {
                const rdays = r.date ? daysBetween(r.date, c.movement_date) : days;
                const byDoc = !!r.doc && docKey(r.doc) === payerDoc(c.description) && rdays >= -2 && rdays <= 15;
                const byAmt = r.amount != null && Math.abs(Number(c.amount) - r.amount) <= 1 && !!r.date && rdays >= -1 && rdays <= 5;
                if (!byDoc && !byAmt) continue;
                const amt = Number(o.total_price || 0);
                const m: Match = { orderId: o.id, creditId: c.id, kind: 'comprobante', diff: Number(c.amount) - amt, amount: amt, days, seña: false,
                    receiptDiff: r.amount != null ? Number(c.amount) - r.amount : undefined };
                if (!best || rank(m) < rank(best)) best = m;
            }
            if (best) { out.push(best); continue; }
            for (const [amt, seña] of amounts) {
                if (amt <= 0) continue;
                const diff = Number(c.amount) - amt;
                const rel = Math.abs(diff) / amt;
                let kind: MatchKind | null = null;
                if (sameDoc && days >= -5 && days <= 30) kind = 'dni';
                else if (Math.abs(diff) <= 1 && days >= -3 && days <= 7) kind = 'exacto';
                else if (rel <= 0.02 && days >= -3 && days <= 7) kind = 'aprox';
                if (!kind) continue;
                const m: Match = { orderId: o.id, creditId: c.id, kind, diff, amount: amt, days, seña };
                if (!best || rank(m) < rank(best)) best = m;
            }
            if (best) out.push(best);
        }
    }
    return out.sort((a, b) => rank(a) - rank(b));
}
const KIND_RANK: Record<MatchKind, number> = { comprobante: 0, dni: 0, exacto: 1, aprox: 2 };
// orden: tipo → cercanía de monto → cercanía de fecha
const rank = (m: Match) => KIND_RANK[m.kind] * 1e6 + Math.min(999, Math.round(Math.abs(m.receiptDiff ?? m.diff) / 100)) * 1e3 + Math.abs(m.days);

/** Mejor pareja por venta y por crédito (greedy: cada uno se usa una vez). */
export function bestPairs(pairs: Match[]): { byOrder: Map<number, Match>; byCredit: Map<number, Match> } {
    const byOrder = new Map<number, Match>(), byCredit = new Map<number, Match>();
    for (const p of pairs) {
        if (byOrder.has(p.orderId) || byCredit.has(p.creditId)) continue;
        byOrder.set(p.orderId, p); byCredit.set(p.creditId, p);
    }
    return { byOrder, byCredit };
}

/** Parejas que se pueden confirmar sin mirar (ver criterio arriba). Un mismo DNI con un monto
 *  muy distinto (seña, pago en partes) se sugiere pero no se confirma solo. */
export function safePairs(pairs: Match[]): Match[] {
    const { byOrder } = bestPairs(pairs);
    const exactOf = (pred: (p: Match) => boolean) => pairs.filter(p => p.kind === 'exacto' && pred(p)).length;
    return Array.from(byOrder.values()).filter(p =>
        (p.kind === 'comprobante' && p.receiptDiff != null && Math.abs(p.receiptDiff) <= 1)
        || (p.kind === 'dni' && Math.abs(p.diff) <= Math.max(1, p.amount * 0.02))
        || (p.kind === 'exacto' && exactOf(x => x.orderId === p.orderId) === 1 && exactOf(x => x.creditId === p.creditId) === 1));
}
